import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  LinearProgress,
  Grid,
  Divider,
  List,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  PlaylistAdd as AddItemIcon,
  ContentCopy as CloneIcon,
  Assignment as AssignmentIcon,
  CloudUpload as UploadIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import api from '../services/api';

const WorkloadFormManager = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  // Category Management States
  const [categories, setCategories] = useState<any[]>([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatCode, setNewCatCode] = useState('');
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    course_code: '',
    curriculum_year: '2567',
    description: '',
    total_max_score: 0,
    is_active: 1
  });

  const [itemData, setItemData] = useState({
    section_name: 'ภาระงาน',
    item_code: '',
    title: '',
    description: '',
    max_score: 10,
    required_submission: true,
    item_type: 'GENERAL',
    item_type_name: 'งานทั่วไป'
  });

  const fetchForms = async () => {
    try {
      setLoading(true);
      const res = await api.get('/workload-forms');
      setForms(res.data);
    } catch (err) {
      console.error('Failed to fetch workload forms:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/workload-forms/categories');
      setCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch workload categories:', err);
    }
  };

  const fetchFormDetails = async (id: string) => {
    try {
      const res = await api.get(`/workload-forms/${id}`);
      setSelectedForm(res.data);
    } catch (err) {
      console.error('Failed to fetch form detail:', err);
    }
  };

  useEffect(() => {
    fetchForms();
    fetchCategories();
  }, []);

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post('/workload-forms/categories', { name: newCatName.trim(), code: newCatCode.trim() });
      setNewCatName('');
      setNewCatCode('');
      fetchCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการเพิ่มประเภทภาระงาน');
    }
  };

  const handleUpdateCategory = async (id: string) => {
    if (!editCatName.trim()) return;
    try {
      await api.put(`/workload-forms/categories/${id}`, { name: editCatName.trim() });
      setEditingCatId(null);
      setEditCatName('');
      fetchCategories();
      if (selectedForm) fetchFormDetails(selectedForm.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการแก้ไขประเภทภาระงาน');
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`ยืนยันลบประเภทภาระงาน "${name}" หรือไม่? รายการที่ใช้ประเภทนี้จะถูกเปลี่ยนเป็น "งานทั่วไป"`)) return;
    try {
      await api.delete(`/workload-forms/categories/${id}`);
      fetchCategories();
      if (selectedForm) fetchFormDetails(selectedForm.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบประเภทภาระงาน');
    }
  };

  const handleOpenFormDialog = (form: any = null) => {
    if (form) {
      setSelectedForm(form);
      setFormData({
        title: form.title,
        course_code: form.course_code || '',
        curriculum_year: form.curriculum_year ? String(form.curriculum_year) : '',
        description: form.description || '',
        total_max_score: form.total_max_score || 0,
        is_active: form.is_active ? 1 : 0
      });
    } else {
      setSelectedForm(null);
      setFormData({
        title: '',
        course_code: '',
        curriculum_year: '2567',
        description: '',
        total_max_score: 0,
        is_active: 1
      });
    }
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
  };

  const handleSaveForm = async () => {
    try {
      if (selectedForm && selectedForm.id) {
        await api.put(`/workload-forms/${selectedForm.id}`, formData);
      } else {
        await api.post('/workload-forms', formData);
      }
      handleCloseFormDialog();
      fetchForms();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกแบบประเมินภาระงาน');
    }
  };

  const handleDeleteForm = async (id: string) => {
    if (window.confirm('คุณต้องการลบแบบประเมินภาระงานนี้ใช่หรือไม่? รายการภาระงานทั้งหมดจะถูกลบออกด้วย')) {
      try {
        await api.delete(`/workload-forms/${id}`);
        if (selectedForm && selectedForm.id === id) {
          setSelectedForm(null);
        }
        fetchForms();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบแบบประเมิน');
      }
    }
  };

  const handleCloneForm = async (id: string) => {
    if (window.confirm('คัดลอกแบบประเมินภาระงานนี้เพื่อเป็นแม่แบบใหม่?')) {
      try {
        setLoading(true);
        await api.post(`/workload-forms/${id}/clone`);
        fetchForms();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการคัดลอก');
      } finally {
        setLoading(false);
      }
    }
  };

  // Item Management
  const handleOpenItemDialog = (item: any = null) => {
    if (item) {
      setEditingItem(item);
      setItemData({
        section_name: item.section_name || 'ภาระงาน',
        item_code: item.item_code || '',
        title: item.title,
        description: item.description || '',
        max_score: item.max_score,
        required_submission: item.required_submission !== 0,
        item_type: item.item_type || (item.title?.includes('วิจัย') ? 'RESEARCH' : 'GENERAL'),
        item_type_name: item.item_type_name || (item.title?.includes('วิจัย') ? 'งานวิจัย' : 'งานทั่วไป')
      });
    } else {
      setEditingItem(null);
      const nextCode = selectedForm && selectedForm.items 
        ? `1.${selectedForm.items.length + 1}`
        : '1.1';
      setItemData({
        section_name: 'ภาระงาน',
        item_code: nextCode,
        title: '',
        description: '',
        max_score: 10,
        required_submission: true,
        item_type: 'GENERAL',
        item_type_name: 'งานทั่วไป'
      });
    }
    setOpenItemDialog(true);
  };

  const handleCloseItemDialog = () => {
    setOpenItemDialog(false);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    if (!selectedForm) return;
    try {
      if (editingItem) {
        await api.put(`/workload-forms/items/${editingItem.id}`, itemData);
      } else {
        await api.post(`/workload-forms/${selectedForm.id}/items`, itemData);
      }
      handleCloseItemDialog();
      fetchFormDetails(selectedForm.id);
      fetchForms();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกรายการภาระงาน');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('ยืนยันลบรายการภาระงานนี้?')) {
      try {
        await api.delete(`/workload-forms/items/${itemId}`);
        if (selectedForm) {
          fetchFormDetails(selectedForm.id);
        }
        fetchForms();
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Drag and Drop & Order Adjustment
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
      setDragOverItemIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverItemIndex(null);
  };

  const persistReorderedItems = async (newItems: any[]) => {
    if (!selectedForm) return;
    try {
      setIsReordering(true);
      const itemIds = newItems.map((item: any) => item.id);
      await api.put(`/workload-forms/${selectedForm.id}/items/reorder`, { itemIds });
    } catch (err) {
      console.error('Failed to save new order:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกลำดับรายการ');
      fetchFormDetails(selectedForm.id);
    } finally {
      setIsReordering(false);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    setDragOverItemIndex(null);
    if (draggedItemIndex === null || draggedItemIndex === targetIndex || !selectedForm?.items) return;

    const items = [...selectedForm.items];
    const [movedItem] = items.splice(draggedItemIndex, 1);
    items.splice(targetIndex, 0, movedItem);

    setSelectedForm({
      ...selectedForm,
      items
    });
    setDraggedItemIndex(null);

    await persistReorderedItems(items);
  };

  const handleMoveItem = async (currentIndex: number, direction: 'up' | 'down') => {
    if (!selectedForm?.items) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= selectedForm.items.length) return;

    const items = [...selectedForm.items];
    const [movedItem] = items.splice(currentIndex, 1);
    items.splice(targetIndex, 0, movedItem);

    setSelectedForm({
      ...selectedForm,
      items
    });

    await persistReorderedItems(items);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            จัดการแบบประเมินภาระงาน (Workload Form Manager)
          </Typography>
          <Typography variant="body2" color="textSecondary">
            สร้างและปรับแต่งเกณฑ์ภาระงานตามรายวิชาและรอบพัฒนาหลักสูตร สำหรับให้อาจารย์นิเทศก์ประเมิน
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<CategoryIcon />} 
            onClick={() => setOpenCategoryDialog(true)}
            sx={{ borderRadius: 2 }}
          >
            จัดการประเภทภาระงาน ({categories.length})
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenFormDialog()}
            sx={{ borderRadius: 2 }}
          >
            สร้างแบบประเมินใหม่
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Forms List (Left Column) */}
        <Grid size={{ xs: 12, md: selectedForm ? 5 : 12 }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
              รายการแบบประเมินภาระงานทั้งหมด ({forms.length})
            </Typography>

            <List disablePadding>
              {forms.map((f: any) => {
                const isSelected = selectedForm && selectedForm.id === f.id;
                return (
                  <Paper
                    key={f.id}
                    elevation={isSelected ? 4 : 1}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3
                      }
                    }}
                    onClick={() => fetchFormDetails(f.id)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {f.title}
                          </Typography>
                          {f.is_active ? (
                            <Chip label="เปิดใช้งาน" size="small" color="success" sx={{ height: 22 }} />
                          ) : (
                            <Chip label="ปิดใช้งาน" size="small" color="default" sx={{ height: 22 }} />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {f.course_code && (
                            <Chip label={`วิชา ${f.course_code}`} size="small" variant="outlined" color="primary" />
                          )}
                          {f.curriculum_year && (
                            <Chip label={`หลักสูตร พ.ศ. ${f.curriculum_year}`} size="small" variant="outlined" />
                          )}
                          <Chip 
                            label={`รวม ${f.total_max_score || f.calculated_total_score || 0} คะแนน (${f.item_count || 0} รายการ)`} 
                            size="small" 
                            color="secondary" 
                            variant="filled" 
                          />
                        </Box>

                        {f.description && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                            {f.description}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="คัดลอกแบบประเมิน">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneForm(f.id);
                            }}
                          >
                            <CloneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="แก้ไขข้อมูลแบบประเมิน">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenFormDialog(f);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ลบแบบประเมิน">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(f.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}

              {forms.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography>ไม่พบแบบประเมินภาระงาน</Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Form Details & Items Management (Right Column) */}
        {selectedForm && (
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedForm.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    คะแนนเต็มรวม: <strong>{selectedForm.total_max_score || 0} คะแนน</strong> | จำนวนภาระงาน: <strong>{selectedForm.items?.length || 0} รายการ</strong>
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  color="success" 
                  size="small"
                  startIcon={<AddItemIcon />}
                  onClick={() => handleOpenItemDialog()}
                >
                  เพิ่มรายการภาระงาน
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <TableContainer component={Box}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 85 }}>ลำดับ/ย้าย</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 65 }}>รหัส</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 110 }}>ประเภท</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>หมวด/ส่วน</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>รายการภาระงาน</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 90 }}>คะแนนเต็ม</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>ส่งไฟล์</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 90 }}>จัดการ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedForm.items && selectedForm.items.map((item: any, index: number) => {
                      const isBeingDragged = draggedItemIndex === index;
                      const isDragOver = dragOverItemIndex === index;
                      const isResearch = item.item_type === 'RESEARCH' || item.item_type_name === 'งานวิจัย' || item.title?.includes('วิจัย');
                      return (
                        <TableRow 
                          key={item.id} 
                          hover
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={() => { setDraggedItemIndex(null); setDragOverItemIndex(null); }}
                          sx={{
                            cursor: 'grab',
                            opacity: isBeingDragged ? 0.35 : 1,
                            bgcolor: isDragOver ? '#e3f2fd' : 'inherit',
                            borderTop: isDragOver ? '2px dashed #1976d2' : undefined,
                            transition: 'background-color 0.15s ease'
                          }}
                        >
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Tooltip title="คลิกลากเพื่อจัดลำดับ (Drag & Drop)">
                                <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'grab', fontSize: 20 }} />
                              </Tooltip>
                              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
                                <Tooltip title="เลื่อนขึ้น">
                                  <span>
                                    <IconButton 
                                      size="small" 
                                      disabled={index === 0 || isReordering} 
                                      onClick={(e) => { e.stopPropagation(); handleMoveItem(index, 'up'); }}
                                      sx={{ p: 0.2 }}
                                    >
                                      <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="เลื่อนลง">
                                  <span>
                                    <IconButton 
                                      size="small" 
                                      disabled={index === selectedForm.items.length - 1 || isReordering} 
                                      onClick={(e) => { e.stopPropagation(); handleMoveItem(index, 'down'); }}
                                      sx={{ p: 0.2 }}
                                    >
                                      <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{item.item_code || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.item_type_name || (isResearch ? 'งานวิจัย' : 'งานทั่วไป')} 
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                bgcolor: isResearch ? 'rgba(124, 58, 237, 0.12)' : 'rgba(30, 64, 175, 0.08)',
                                color: isResearch ? '#7C3AED' : '#1E40AF',
                                border: '1px solid',
                                borderColor: isResearch ? 'rgba(124, 58, 237, 0.3)' : 'rgba(30, 64, 175, 0.2)'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={item.section_name || 'ภาระงาน'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.title}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                {item.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {Number(item.max_score) > 0 ? (
                              <Chip 
                                label={`${item.max_score} คะแนน`} 
                                size="small" 
                                color="primary" 
                                variant="filled" 
                                sx={{ fontWeight: 700 }}
                              />
                            ) : (
                              <Chip 
                                label="ไม่คิดคะแนน (ส่งหลักฐาน)" 
                                size="small" 
                                color="info" 
                                variant="outlined" 
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {item.required_submission !== 0 ? (
                              <Tooltip title="นักศึกษาต้องแนบไฟล์ส่งงาน">
                                <UploadIcon color="primary" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="textSecondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={() => handleOpenItemDialog(item)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {(!selectedForm.items || selectedForm.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          ยังไม่มีรายการภาระงาน คลิก "เพิ่มรายการภาระงาน" เพื่อเริ่มต้น
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Form Add / Edit Modal */}
      <Dialog open={openFormDialog} onClose={handleCloseFormDialog} fullWidth maxWidth="sm">
        <DialogTitle>{selectedForm && selectedForm.id ? 'แก้ไขแบบประเมินภาระงาน' : 'สร้างแบบประเมินภาระงานใหม่'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="ชื่อแบบประเมินภาระงาน"
                placeholder="เช่น แบบประเมินภาระงาน 1002101 การปฏิบัติการสอนในสถานศึกษา 1"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="รหัสวิชาอ้างอิง"
                placeholder="เช่น 1002101, 1004103"
                value={formData.course_code}
                onChange={(e) => setFormData({ ...formData, course_code: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="ปีพัฒนาหลักสูตร (พ.ศ.)"
                placeholder="เช่น 2562, 2567"
                type="number"
                value={formData.curriculum_year}
                onChange={(e) => setFormData({ ...formData, curriculum_year: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="คำอธิบายแบบประเมิน"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active === 1}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                    color="primary"
                  />
                }
                label="เปิดใช้งานแบบประเมินนี้"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseFormDialog}>ยกเลิก</Button>
          <Button onClick={handleSaveForm} variant="contained" color="primary">บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Item Add / Edit Modal */}
      <Dialog open={openItemDialog} onClose={handleCloseItemDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingItem ? 'แก้ไขรายการภาระงาน' : 'เพิ่มรายการภาระงาน'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>ประเภทภาระงาน</InputLabel>
                <Select
                  value={itemData.item_type_name}
                  label="ประเภทภาระงาน"
                  onChange={(e) => {
                    const selCat = categories.find((c: any) => c.name === e.target.value);
                    setItemData({
                      ...itemData,
                      item_type_name: e.target.value,
                      item_type: selCat?.code || (e.target.value === 'งานวิจัย' ? 'RESEARCH' : 'GENERAL')
                    });
                  }}
                >
                  {categories.map((cat: any) => (
                    <MenuItem key={cat.id} value={cat.name}>
                      {cat.name} {cat.code === 'RESEARCH' ? ' (เชื่อมโยงฐานข้อมูลวิจัย)' : ''}
                    </MenuItem>
                  ))}
                  {categories.length === 0 && (
                    <MenuItem value="งานทั่วไป">งานทั่วไป</MenuItem>
                  )}
                  {categories.length === 0 && (
                    <MenuItem value="งานวิจัย">งานวิจัย (เชื่อมโยงฐานข้อมูลวิจัย)</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="รหัสข้อ (Item Code)"
                placeholder="เช่น 1.1, 1.2"
                value={itemData.item_code}
                onChange={(e) => setItemData({ ...itemData, item_code: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="หมวด / ส่วนของภาระงาน"
                placeholder="เช่น เล่มบันทึกประจำตัว, รายงานการสังเกต, งานวิจัยในชั้นเรียน"
                value={itemData.section_name}
                onChange={(e) => setItemData({ ...itemData, section_name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="ชื่อรายการภาระงาน"
                placeholder="เช่น แผนการสอน, บันทึกประจำตัว, วิจัยในชั้นเรียน, บทความวิจัย"
                value={itemData.title}
                onChange={(e) => setItemData({ ...itemData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="คะแนนเต็ม (0 = ไม่คิดคะแนน)"
                type="number"
                value={itemData.max_score}
                onChange={(e) => setItemData({ ...itemData, max_score: e.target.value === '' ? 0 : Number(e.target.value) })}
                slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Number(itemData.max_score) > 0}
                    onChange={(e) => setItemData({ ...itemData, max_score: e.target.checked ? 10 : 0 })}
                    color="primary"
                  />
                }
                label={Number(itemData.max_score) > 0 ? `กำหนดให้มีการคิดคะแนน (${itemData.max_score} คะแนน)` : 'ไม่กำหนดคะแนน (ส่งงานเพื่อเก็บหลักฐานอย่างเดียว เช่น บทความวิจัย)'}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="คำอธิบายเกณฑ์หรือแนวทางการส่งงาน"
                value={itemData.description}
                onChange={(e) => setItemData({ ...itemData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={itemData.required_submission}
                    onChange={(e) => setItemData({ ...itemData, required_submission: e.target.checked })}
                    color="primary"
                  />
                }
                label="กำหนดให้นักศึกษาแนบไฟล์หลักฐาน / ส่งงานในระบบ"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseItemDialog}>ยกเลิก</Button>
          <Button onClick={handleSaveItem} variant="contained" color="primary">บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon color="primary" />
          จัดการประเภทของภาระงาน (Workload Item Categories)
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            กำหนดชื่อประเภทภาระงาน เช่น <strong>งานทั่วไป</strong> หรือ <strong>งานวิจัย</strong> เพื่อให้เชื่อมโยงผลงานนักศึกษากับระบบฐานข้อมูลงานวิจัยในชั้นเรียนโดยอัตโนมัติ
          </Typography>

          {/* Add Category Form */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#F8FAFC' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              เพิ่มประเภทภาระงานใหม่
            </Typography>
            <Grid container spacing={1.5} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ชื่อประเภทภาระงาน"
                  placeholder="เช่น งานวิจัย, สื่อการสอน"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="รหัสโค้ด (Optional)"
                  placeholder="เช่น RESEARCH"
                  value={newCatCode}
                  onChange={(e) => setNewCatCode(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={handleCreateCategory}
                  disabled={!newCatName.trim()}
                >
                  เพิ่ม
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Existing Categories Table */}
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ชื่อประเภทภาระงาน</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>รหัส</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>สถานะระบบ</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, width: 90 }}>จัดการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat: any) => {
                const isEditing = editingCatId === cat.id;
                return (
                  <TableRow key={cat.id} hover>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={editCatName}
                          onChange={(e) => setEditCatName(e.target.value)}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={cat.name} 
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: cat.code === 'RESEARCH' ? 'rgba(124, 58, 237, 0.12)' : 'rgba(30, 64, 175, 0.08)',
                              color: cat.code === 'RESEARCH' ? '#7C3AED' : '#1E40AF',
                            }}
                          />
                          {cat.code === 'RESEARCH' && (
                            <Typography variant="caption" color="textSecondary">
                              (เชื่อมโยงระบบวิจัย)
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                      {cat.code || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {cat.is_system ? (
                        <Chip label="ประเภทหลัก" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      ) : (
                        <Chip label="ผู้ใช้สร้าง" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" color="primary" onClick={() => handleUpdateCategory(cat.id)}>
                            บันทึก
                          </Button>
                          <Button size="small" onClick={() => setEditingCatId(null)}>
                            ยกเลิก
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                              setEditingCatId(cat.id);
                              setEditCatName(cat.name);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!cat.is_system && (
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteCategory(cat.id, cat.name)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCategoryDialog(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkloadFormManager;
